import React, { useState, useEffect, useRef } from 'react';
import {
    Leaf, Users, Search, Bell, PhoneCall, LayoutGrid, MessageSquare,
    CalendarCheck, FileText, ClipboardCheck, HeartPulse, List,
    ChevronLeft, ChevronRight, Pen, Phone, CalendarPlus, ChevronDown,
    Check, Image as ImageIcon, ZoomIn, UploadCloud, AlertTriangle,
    BarChart2, PieChart as PieChartIcon, Plus, Play, Paperclip, Star
} from 'lucide-react';
import {
    BarChart, Bar, Line, ComposedChart, XAxis, YAxis, Tooltip,
    ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid
} from 'recharts';

// --- Dữ liệu Mock cho Biểu đồ ---
const monthlySpendingData = [
    { name: 'T8/25', spend: 1.5, avg: 1.95 },
    { name: 'T9/25', spend: 2.1, avg: 1.95 },
    { name: 'T10/25', spend: 0.8, avg: 1.95 },
    { name: 'T11/25', spend: 3.2, avg: 1.95 },
    { name: 'T12/25', spend: 5.0, avg: 1.95 },
    { name: 'T1/26', spend: 2.4, avg: 1.95 },
    { name: 'T2/26', spend: 2.0, avg: 1.95 },
    { name: 'T3/26', spend: 0, avg: 1.95 },
    { name: 'T4/26', spend: 0, avg: 1.95 },
    { name: 'T5/26', spend: 4.0, avg: 1.95 },
    { name: 'T6/26', spend: 0.5, avg: 1.95 },
    { name: 'T7/26', spend: 1.8, avg: 1.95 }
];

const serviceSpendingData = [
    { name: 'Gói liệu trình', value: 40, color: '#6366f1' },
    { name: 'Massage body', value: 1.5, color: '#10b981' },
    { name: 'Tắm trắng toàn thân', value: 4.2, color: '#f59e0b' },
    { name: 'Chăm sóc da mặt', value: 16.6, color: '#0ea5e9' }
];

const callStatusData = [
    { name: 'Nghe máy', value: 15, color: '#10b981' },
    { name: 'Không nghe máy', value: 6, color: '#f43f5e' },
    { name: 'Máy bận / Hủy', value: 3, color: '#f59e0b' }
];

const appointmentMonthsData = [
    { name: 'T1', count: 1 }, { name: 'T2', count: 0 }, { name: 'T3', count: 2 },
    { name: 'T4', count: 1 }, { name: 'T5', count: 1 }, { name: 'T6', count: 2 },
    { name: 'T7', count: 1 }, { name: 'T8', count: 1 }, { name: 'T9', count: 3 },
    { name: 'T10', count: 2 }, { name: 'T11', count: 1 }, { name: 'T12', count: 1 }
];

const appointmentStatusData = [
    { name: 'Lịch mới', value: 3, color: '#0ea5e9' },
    { name: 'Đã xác nhận', value: 4, color: '#a855f7' },
    { name: 'Chờ thực hiện', value: 2, color: '#f59e0b' },
    { name: 'Đã thực hiện', value: 5, color: '#10b981' },
    { name: 'Hủy lịch', value: 2, color: '#f43f5e' }
];

const serviceTurnData = [
    { name: '14/04', value: 980 },
    { name: '02/05', value: 250 },
    { name: '20/05', value: 1800 },
    { name: '29/06', value: 650 },
    { name: '18/07', value: 1200 }
];

const paymentMethodData = [
    { name: 'Chuyển khoản', value: 2.18, color: '#4f46e5' },
    { name: 'Thẻ', value: 1.8, color: '#ec4899' },
    { name: 'Tiền mặt', value: 0.9, color: '#f59e0b' }
];

const careFrequencyData = [
    { name: 'CS da mặt collagen', value: 12, color: '#4f46e5' },
    { name: 'Gói ủ trắng body', value: 8, color: '#10b981' },
    { name: 'Massage đá nóng 90p', value: 5, color: '#f59e0b' },
    { name: 'Tẩy tế bào chết TT', value: 3, color: '#0ea5e9' }
];

// --- Cấu hình Dropdown Trạng thái CS ---
const csStatuses = [
    { id: 'S0', text: 'S0 - Khách hàng mới', bg: 'bg-[#3b82f6]' },
    { id: 'S1', text: 'S1 - Tò mò, nhận thức kém', bg: 'bg-[#4b5563]' },
    { id: 'S2', text: 'S2 - Tìm hiểu sơ bộ', bg: 'bg-[#f59e0b]' },
    { id: 'S3', text: 'S3 - Có nhu cầu rõ ràng nhưng còn rào cản', bg: 'bg-[#d97706]' },
    { id: 'S4', text: 'S4 - Nhu cầu cao, cân nhắc lựa chọn', bg: 'bg-[#0ea5e9]' }
];

const chatTags = [
    { text: 'Chăm sóc lại', bg: 'bg-[#e04f33]' },
    { text: 'Mới', bg: 'bg-[#408a9f]' },
    { text: 'Upsale', bg: 'bg-[#19b986]' },
    { text: 'Khai thác thêm', bg: 'bg-[#d2ab20]' },
    { text: 'Gia hạn', bg: 'bg-[#85c31f]' },
    { text: 'Để lại số', bg: 'bg-[#ec871c]' }
];

export default function CRMDashboard() {
    // State quản lý Dropdowns
    const [csStatus, setCsStatus] = useState(csStatuses[0]);
    const [isCsMenuOpen, setIsCsMenuOpen] = useState(false);
    const csMenuRef = useRef(null);

    const [chatTag, setChatTag] = useState(null);
    const [isChatTagMenuOpen, setIsChatTagMenuOpen] = useState(false);
    const chatTagMenuRef = useRef(null);

    // State quản lý View (Chart vs List)
    const [appointmentView, setAppointmentView] = useState('chart');
    const [serviceView, setServiceView] = useState('chart');
    const [notesView, setNotesView] = useState('chart');

    // Đóng dropdown khi click ra ngoài
    useEffect(() => {
        function handleClickOutside(event) {
            if (csMenuRef.current && !csMenuRef.current.contains(event.target)) {
                setIsCsMenuOpen(false);
            }
            if (chatTagMenuRef.current && !chatTagMenuRef.current.contains(event.target)) {
                setIsChatTagMenuOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            // payload[0] là data của cột được hover
            const data = payload[0].payload;

            // Tìm giá trị chi tiêu lớn nhất trong mảng để check tự động
            // Hoặc bạn có thể hardcode isMax = data.spend === 5.0 dựa theo data hiện tại
            const maxSpend = Math.max(...monthlySpendingData.map(item => item.spend));
            const isMax = data.spend === maxSpend;

            return (
                <div className="bg-gray-900/90 rounded-lg p-3 text-white shadow-lg text-sm border-none min-w-[180px]">
                    {/* Tháng */}
                    <p className="font-bold mb-2">{label}</p>

                    {/* Dòng hiển thị chi tiêu */}
                    <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 bg-indigo-500 rounded-sm"></span>
                        <span>Chi tiêu: {data.spend} Triệu</span>
                    </div>

                    {/* Điều kiện hiển thị Ngôi sao: Chỉ hiện khi là tháng có chi tiêu cao nhất */}
                    {isMax && (
                        <div className="flex items-center gap-1.5 mt-3 text-yellow-400 font-medium">
                            <Star className="w-4 h-4 text-yellow-400" fill="currentColor" />
                            <span>Tháng chi tiêu cao nhất</span>
                        </div>
                    )}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="pb-10">

            {/* Row 1: Chi tiêu tháng & Chi tiêu dịch vụ */}
            <div className="flex flex-col lg:flex-row gap-4 mb-8 mt-2">
                {/* Chi tiêu theo tháng */}
                <div className="flex-1 bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col p-5">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="font-bold text-gray-800 flex items-center gap-2 text-base">
                            <BarChart2 className="text-indigo-500 w-5 h-5" /> Chi tiêu theo tháng
                        </h3>
                        <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-md">12 tháng gần nhất</span>
                    </div>
                    <div className="h-[220px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={monthlySpendingData} margin={{ top: 10, right: 0, left: -35, bottom: 0 }}>
                                <XAxis dataKey="name" tickLine={false} tick={{ fontSize: 11, fill: '#6b7280' }} />
                                <YAxis tickLine={false} tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={(val) => `${val}tr`} />

                                {/* Sử dụng Custom Tooltip tại đây */}
                                <Tooltip cursor={{ fill: 'rgba(30, 58, 138, 0.1)' }} content={<CustomTooltip />} />

                                <Bar dataKey="spend" radius={[6, 6, 6, 6]} barSize={16}>
                                    {monthlySpendingData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.spend === 5.0 ? '#4f46e5' : entry.spend === 0 ? '#e5e7eb' : '#a5b4fc'} />
                                    ))}
                                </Bar>
                                <Line type="monotone" dataKey="avg" stroke="#f43f5e" strokeWidth={2} strokeDasharray="5 5" dot={false} activeDot={false} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Chi tiêu theo dịch vụ */}
                <div className="flex-1 bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col p-5">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="font-bold text-gray-800 flex items-center gap-2 text-base">
                            <PieChartIcon className="text-indigo-500 w-5 h-5" /> Chi tiêu theo dịch vụ
                        </h3>
                        <span className="text-xs font-medium text-gray-500">Tổng 62.300.000đ</span>
                    </div>
                    <div className="flex flex-col sm:flex-row items-center justify-between h-auto sm:h-[220px] gap-4">
                        <div className="w-full sm:w-1/2 h-[200px] sm:h-full relative">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={serviceSpendingData} cx="50%" cy="50%" innerRadius="65%" outerRadius="90%" paddingAngle={2} dataKey="value" stroke="none">
                                        {serviceSpendingData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.9)', borderRadius: '8px', border: 'none', color: '#fff' }}
                                        itemStyle={{ color: '#fff' }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="w-full sm:w-1/2 pl-0 sm:pl-4 flex flex-col justify-center gap-3 sm:gap-4">
                            {[
                                { name: 'Gói liệu trình', value: '40.000.000đ', color: 'bg-indigo-500' },
                                { name: 'Massage body', value: '1.500.000đ', color: 'bg-emerald-500' },
                                { name: 'Tắm trắng TT', value: '4.200.000đ', color: 'bg-amber-500' },
                                { name: 'Chăm sóc da mặt', value: '16.600.000đ', color: 'bg-sky-500' }
                            ].map((item, idx) => (
                                <div key={idx} className="flex justify-between items-center text-sm">
                                    <div className="flex items-center gap-2">
                                        <span className={`w-3 h-3 rounded-full ${item.color}`}></span>
                                        <span className="text-gray-600">{item.name}</span>
                                    </div>
                                    <span className="font-semibold text-gray-800">{item.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="mb-8">
                <div className="flex items-baseline gap-2 mb-4 px-1">
                    <h3 className="text-xl text-000000e0 serif-title">Thông tin & Lịch sử cuộc gọi</h3>
                </div>

                <div className="flex flex-col lg:flex-row gap-4">
                    {/* Biểu đồ trạng thái gọi */}
                    <div className="w-full lg:w-[320px] shrink-0 bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6 flex flex-col">
                        <h4 className="font-bold text-gray-800 text-sm mb-6 flex items-center gap-2">
                            <PieChartIcon className="text-indigo-500 w-4 h-4" /> Thống kê trạng thái gọi
                        </h4>
                        <div className="flex-1 flex flex-col items-center justify-center">
                            <div className="h-[150px] w-[150px] relative mb-6">
                                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                    <span className="font-bold text-2xl text-gray-800">24</span>
                                    <span className="text-[10px] text-gray-500 font-medium">CUỘC GỌI</span>
                                </div>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={callStatusData} cx="50%" cy="50%" innerRadius="70%" outerRadius="95%" dataKey="value" stroke="none">
                                            {callStatusData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                                        </Pie>
                                        <Tooltip contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.9)', borderRadius: '8px', border: 'none', color: '#fff' }}
                                            itemStyle={{ color: '#fff' }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="w-full space-y-3 text-sm px-2">
                                {[
                                    { name: 'Nghe máy', value: 15, color: 'bg-emerald-500' },
                                    { name: 'Không nghe máy', value: 6, color: 'bg-rose-500' },
                                    { name: 'Máy bận / Hủy', value: 3, color: 'bg-amber-500' }
                                ].map((item, idx) => (
                                    <div key={idx} className="flex justify-between items-center">
                                        <div className="flex items-center gap-2"><span className={`w-3 h-3 rounded-full ${item.color}`}></span><span className="text-gray-600">{item.name}</span></div>
                                        <span className="font-semibold text-gray-800">{item.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Danh sách nội dung chăm sóc */}
                    <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col overflow-hidden">
                        <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 shrink-0">
                            <h4 className="font-bold text-gray-800 text-sm flex items-center gap-2">
                                <List className="text-indigo-500 w-4 h-4" /> Nội dung chăm sóc khách hàng
                            </h4>
                            <button className="bg-indigo-50 text-indigo-600 hover:bg-indigo-100 hover:text-indigo-700 text-xs font-medium px-3 py-1.5 rounded transition-colors flex items-center gap-1.5 border border-indigo-100">
                                <Plus className="w-3.5 h-3.5" /> Thêm ghi chú
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-5 h-[260px] space-y-4">
                            {/* Item 1 */}
                            <div className="flex gap-4">
                                <div className="flex flex-col items-center mt-1">
                                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 ring-4 ring-emerald-50"></div>
                                    <div className="w-px h-full bg-gray-200 my-1"></div>
                                </div>
                                <div className="flex-1 bg-white border border-gray-100 shadow-sm rounded-lg p-4 mb-2 hover:border-indigo-200 transition-colors">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <span className="font-bold text-gray-800 text-sm">tele admin</span>
                                            <span className="text-xs text-gray-400 font-medium ml-2">Hôm qua, 14:30</span>
                                        </div>
                                        <span className="bg-emerald-50 text-emerald-600 text-[10px] font-bold px-2 py-0.5 rounded border border-emerald-200 uppercase tracking-wide">Nghe máy</span>
                                    </div>
                                    <p className="text-sm text-gray-600 mb-3 leading-relaxed">Khách hàng quan tâm đến liệu trình trẻ hóa da mặt. Đã tư vấn sơ bộ về các gói dịch vụ và gửi báo giá kèm hình ảnh ưu đãi qua Zalo. Khách hẹn sẽ xem và báo lại vào cuối tuần.</p>
                                    <div className="flex items-center gap-3">
                                        <button className="flex items-center gap-1.5 px-3 py-1 bg-gray-50 border border-gray-200 rounded-full text-xs font-medium text-gray-600 hover:text-indigo-600 hover:border-indigo-300 transition-colors">
                                            <Play className="text-indigo-500 w-3 h-3" /> 02:15
                                        </button>
                                        <button className="flex items-center gap-1 text-xs font-medium text-indigo-500 hover:text-indigo-700 hover:underline">
                                            <Paperclip className="w-3 h-3" /> Zalo_BaoGia.png
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Item 2 */}
                            <div className="flex gap-4">
                                <div className="flex flex-col items-center mt-1">
                                    <div className="w-2.5 h-2.5 rounded-full bg-rose-400 ring-4 ring-rose-50"></div>
                                    <div className="w-px h-full bg-gray-200 my-1"></div>
                                </div>
                                <div className="flex-1 bg-white border border-gray-100 shadow-sm rounded-lg p-4 mb-2 hover:border-indigo-200 transition-colors">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <span className="font-bold text-gray-800 text-sm">tp03</span>
                                            <span className="text-xs text-gray-400 font-medium ml-2">25/07/2026, 09:15</span>
                                        </div>
                                        <span className="bg-rose-50 text-rose-600 text-[10px] font-bold px-2 py-0.5 rounded border border-rose-200 uppercase tracking-wide">Không nghe máy</span>
                                    </div>
                                    <p className="text-sm text-gray-600 leading-relaxed">Gọi điện nhắc lịch hẹn tái khám ngày mai nhưng khách không bắt máy, thuê bao đổ chuông dài. Đã chủ động để lại tin nhắn SMS nhắc lịch.</p>
                                </div>
                            </div>

                            {/* Item 3 */}
                            <div className="flex gap-4">
                                <div className="flex flex-col items-center mt-1">
                                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 ring-4 ring-emerald-50"></div>
                                    <div className="w-px h-full bg-transparent my-1"></div>
                                </div>
                                <div className="flex-1 bg-white border border-gray-100 shadow-sm rounded-lg p-4 mb-2 hover:border-indigo-200 transition-colors">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <span className="font-bold text-gray-800 text-sm">TV Huyền</span>
                                            <span className="text-xs text-gray-400 font-medium ml-2">20/07/2026, 16:45</span>
                                        </div>
                                        <span className="bg-emerald-50 text-emerald-600 text-[10px] font-bold px-2 py-0.5 rounded border border-emerald-200 uppercase tracking-wide">Nghe máy</span>
                                    </div>
                                    <p className="text-sm text-gray-600 mb-3 leading-relaxed">Gọi điện hỏi thăm tình trạng da sau khi làm dịch vụ Tắm trắng. Khách phản hồi da sáng lên nhiều, mềm mịn và không bị kích ứng. Rất hài lòng với dịch vụ và thái độ của KTV.</p>
                                    <div className="flex items-center gap-3">
                                        <button className="flex items-center gap-1.5 px-3 py-1 bg-gray-50 border border-gray-200 rounded-full text-xs font-medium text-gray-600 hover:text-indigo-600 hover:border-indigo-300 transition-colors">
                                            <Play className="text-indigo-500 w-3 h-3" /> 05:40
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 px-1">
                    <div className="flex items-baseline gap-2">
                        <h3 className="text-xl text-000000e0 serif-title">Lịch hẹn</h3>
                    </div>
                    <div className="bg-gray-200/60 p-1 rounded-full flex gap-1">
                        <button onClick={() => setAppointmentView('chart')} className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all ${appointmentView === 'chart' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>Biểu đồ</button>
                        <button onClick={() => setAppointmentView('list')} className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all ${appointmentView === 'list' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>Danh sách</button>
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                    {appointmentView === 'chart' ? (
                        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                            {/* Bar Chart: Số lịch hẹn 12 tháng */}
                            <div className="flex-1 border-b lg:border-b-0 lg:border-r border-gray-100 pb-6 lg:pb-0 pr-0 lg:pr-8">
                                <h4 className="text-sm font-medium text-gray-500 mb-6">Số lịch hẹn theo tháng (T1 - T12/2026)</h4>
                                <div className="h-[220px] w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={appointmentMonthsData} margin={{ top: 15, right: 0, left: -60, bottom: 0 }}>
                                            <XAxis dataKey="name" tickLine={false} tick={{ fontSize: 11, fill: '#6b7280' }} />
                                            <YAxis axisLine={false} tickLine={false} tick={false} />
                                            <Tooltip
                                                cursor={{ fill: 'transparent' }}
                                                contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.9)', borderRadius: '8px', border: 'none', color: '#fff' }}
                                                itemStyle={{ color: '#fff' }}
                                            />
                                            <Bar dataKey="count" fill="#4f46e5" radius={[4, 4, 4, 4]} barSize={14}>
                                                {/* Tuỳ chỉnh nhãn trên cột bằng Recharts */}
                                                {appointmentMonthsData.map((entry, index) => (
                                                    entry.count > 0 && <Cell key={`cell-${index}`} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                            {/* Doughnut Chart: Tỷ lệ theo trạng thái */}
                            <div className="w-full lg:w-[300px] shrink-0 flex flex-col justify-center">
                                <h4 className="text-sm font-medium text-gray-500 mb-6 text-center">Tỷ lệ theo trạng thái</h4>
                                <div className="flex items-center gap-6">
                                    <div className="h-[140px] w-[140px] relative">
                                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                            <span className="font-bold text-xl text-gray-800">16</span>
                                            <span className="text-[9px] text-gray-500 font-medium uppercase mt-0.5">Lịch hẹn</span>
                                        </div>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={appointmentStatusData} cx="50%" cy="50%" innerRadius="70%" outerRadius="95%" dataKey="value" stroke="none">
                                                    {appointmentStatusData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                                                </Pie>
                                                <Tooltip contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.9)', borderRadius: '8px', border: 'none', color: '#fff' }}
                                                    itemStyle={{ color: '#fff' }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className="space-y-3 text-sm flex-1">
                                        {appointmentStatusData.map((item, idx) => (
                                            <div key={idx} className="flex justify-between items-center">
                                                <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></span><span className="text-gray-600 text-xs">{item.name}</span></div>
                                                <span className="font-medium text-gray-800 text-xs">{item.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto table-scroll">
                            <table className="w-full min-w-[800px] text-left border-collapse min-w-[600px]">
                                <thead>
                                    <tr className="text-xs text-gray-500 uppercase tracking-wider border-b border-gray-100">
                                        <th className="pb-3 font-medium">Ngày</th>
                                        <th className="pb-3 font-medium">Dịch vụ</th>
                                        <th className="pb-3 font-medium">Trực page</th>
                                        <th className="pb-3 font-medium">Telesale</th>
                                        <th className="pb-3 font-medium">Tư vấn</th>
                                        <th className="pb-3 font-medium">KTV</th>
                                        <th className="pb-3 font-medium text-right">Trạng thái</th>
                                    </tr>
                                </thead>
                                <tbody className="text-sm divide-y divide-gray-100">
                                    {[
                                        { date: '02/08/2026', service: 'Massage đá nóng 90 phút', page: 'tp03', tele: 'tele01', tuvan: 'TV Huyền', ktv: 'KTV Lan', status: 'Sắp tới', statusColor: 'bg-amber-50 text-amber-600 border-amber-200/60' },
                                        { date: '18/07/2026', service: 'Chăm sóc da mặt collagen', page: 'tp01', tele: '-', tuvan: '-', ktv: 'KTV Mai', status: 'Đã hoàn thành', statusColor: 'bg-emerald-50 text-emerald-600 border-emerald-200/60' },
                                        { date: '29/06/2026', service: 'Massage Thụy Điển 60 phút', page: 'tp03', tele: 'tele admin', tuvan: 'TV Huyền', ktv: 'KTV Lan', status: 'Đã hoàn thành', statusColor: 'bg-emerald-50 text-emerald-600 border-emerald-200/60' },
                                        { date: '05/06/2026', service: 'Tẩy tế bào chết toàn thân', page: 'tp02', tele: '-', tuvan: 'TV Oanh', ktv: 'KTV Hương', status: 'Đã huỷ', statusColor: 'bg-rose-50 text-rose-600 border-rose-200/60' },
                                        { date: '20/05/2026', service: 'Gói ủ trắng body', page: 'tp03', tele: 'tele02', tuvan: '-', ktv: 'KTV Mai', status: 'Đã hoàn thành', statusColor: 'bg-emerald-50 text-emerald-600 border-emerald-200/60' },
                                    ].map((row, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50/50 transition-colors group">
                                            <td className="py-4 font-medium text-gray-600">{row.date}</td>
                                            <td className="py-4 text-gray-800 font-medium group-hover:text-indigo-600 transition-colors">{row.service}</td>
                                            <td className="py-4 text-gray-600">{row.page}</td>
                                            <td className="py-4 text-gray-600">{row.tele}</td>
                                            <td className="py-4 text-gray-600">{row.tuvan}</td>
                                            <td className="py-4 text-gray-600">{row.ktv}</td>
                                            <td className="py-4 text-right"><span className={`inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs font-medium border ${row.statusColor}`}>{row.status}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <div className="mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 px-1">
                    <div className="flex items-baseline gap-2">
                        <h3 className="text-xl text-000000e0 serif-title">Chi tiêu theo từng dịch vụ</h3>
                    </div>
                    <div className="bg-gray-200/60 p-1 rounded-full flex gap-1">
                        <button onClick={() => setServiceView('chart')} className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all ${serviceView === 'chart' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>Biểu đồ</button>
                        <button onClick={() => setServiceView('list')} className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all ${serviceView === 'list' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>Danh sách</button>
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                    {serviceView === 'chart' ? (
                        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                            <div className="flex-1 border-b lg:border-b-0 lg:border-r border-gray-100 pb-6 lg:pb-0 pr-0 lg:pr-8">
                                <h4 className="text-sm font-medium text-gray-500 mb-6">Chi tiêu theo từng lượt (nghìn đồng)</h4>
                                <div className="h-[220px] w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={serviceTurnData} margin={{ top: 15, right: 0, left: -60, bottom: 0 }}>
                                            <XAxis dataKey="name" tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
                                            <YAxis axisLine={false} tickLine={false} tick={false} />
                                            <Tooltip cursor={{ fill: 'transparent' }} contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.9)', borderRadius: '8px', border: 'none', color: '#fff' }}
                                                itemStyle={{ color: '#fff' }}
                                            />
                                            <Bar dataKey="value" fill="#4f46e5" radius={[4, 4, 4, 4]} barSize={28} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                            <div className="w-full lg:w-[300px] shrink-0 flex flex-col justify-center">
                                <h4 className="text-sm font-medium text-gray-500 mb-6 text-center">Tỷ trọng theo hình thức thanh toán</h4>
                                <div className="flex items-center gap-6">
                                    <div className="h-[140px] w-[140px] relative">
                                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                            <span className="font-bold text-xl text-gray-800">4,9tr</span>
                                            <span className="text-[9px] text-gray-500 font-medium uppercase mt-0.5">Tổng</span>
                                        </div>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={paymentMethodData} cx="50%" cy="50%" innerRadius="70%" outerRadius="95%" dataKey="value" stroke="none">
                                                    {paymentMethodData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                                                </Pie>
                                                <Tooltip contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.9)', borderRadius: '8px', border: 'none', color: '#fff' }}
                                                    itemStyle={{ color: '#fff' }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className="space-y-4 text-sm flex-1">
                                        {paymentMethodData.map((item, idx) => (
                                            <div key={idx} className="flex justify-between items-center">
                                                <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></span><span className="text-gray-600 text-xs">{item.name}</span></div>
                                                <span className="font-medium text-gray-800 text-xs">{item.value}tr</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto table-scroll">
                            <table className="w-full text-left border-collapse min-w-[600px]">
                                <thead>
                                    <tr className="text-xs text-gray-500 uppercase tracking-wider border-b border-gray-100">
                                        <th className="pb-3 font-medium w-1/4">Ngày</th>
                                        <th className="pb-3 font-medium w-1/3">Dịch vụ</th>
                                        <th className="pb-3 font-medium w-1/4">Hình thức</th>
                                        <th className="pb-3 font-medium text-right">Số tiền</th>
                                    </tr>
                                </thead>
                                <tbody className="text-sm divide-y divide-gray-100">
                                    {[
                                        { date: '18/07/2026', service: 'Chăm sóc da mặt collagen', method: 'Chuyển khoản', amount: '1.200.000 đ' },
                                        { date: '29/06/2026', service: 'Massage Thụy Điển 60p', method: 'Tiền mặt', amount: '650.000 đ' },
                                        { date: '20/05/2026', service: 'Gói ủ trắng body', method: 'Thẻ', amount: '1.800.000 đ' },
                                        { date: '02/05/2026', service: 'Gội đầu dưỡng sinh', method: 'Tiền mặt', amount: '250.000 đ' },
                                        { date: '14/04/2026', service: 'Massage đá nóng 90p', method: 'Chuyển khoản', amount: '980.000 đ' },
                                    ].map((row, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50/50 transition-colors group">
                                            <td className="py-4 font-medium text-gray-600">{row.date}</td>
                                            <td className="py-4 text-gray-800 font-medium group-hover:text-indigo-600 transition-colors">{row.service}</td>
                                            <td className="py-4 text-gray-600">{row.method}</td>
                                            <td className="py-4 text-right font-medium text-gray-800">{row.amount}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="3" className="pt-4 font-bold text-gray-800 text-sm border-t border-gray-100">Tổng chi tiêu</td>
                                        <td className="pt-4 font-bold text-gray-800 text-right border-t border-gray-100">4.880.000 đ</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <div className="mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 px-1">
                    <div className="flex items-baseline gap-2">
                        <h3 className="text-xl text-000000e0 serif-title">Ghi chú làm việc & Tần suất chăm sóc</h3>
                    </div>
                    <div className="bg-gray-200/60 p-1 rounded-full flex gap-1">
                        <button onClick={() => setNotesView('chart')} className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all ${notesView === 'chart' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>Biểu đồ</button>
                        <button onClick={() => setNotesView('list')} className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all ${notesView === 'list' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>Danh sách</button>
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                    {notesView === 'chart' ? (
                        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                            <div className="flex-1">
                                <h4 className="text-sm font-medium text-gray-500 mb-6">Số lượng cuộc gọi chăm sóc theo từng dịch vụ</h4>
                                <div className="h-[220px] w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={careFrequencyData} margin={{ top: 15, right: 0, left: -20, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
                                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
                                            <Tooltip cursor={{ fill: 'rgba(0,0,0,0.05)' }} contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.9)', borderRadius: '8px', border: 'none', color: '#fff' }}
                                                itemStyle={{ color: '#fff' }}
                                            />
                                            <Bar dataKey="value" radius={[6, 6, 6, 6]} barSize={40}>
                                                {careFrequencyData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto table-scroll">
                            <table className="w-full text-left border-collapse min-w-[600px]">
                                <thead>
                                    <tr className="text-xs text-gray-500 uppercase tracking-wider border-b border-gray-100">
                                        <th className="pb-3 font-medium">Dịch vụ</th>
                                        <th className="pb-3 font-medium text-center">Số cuộc gọi</th>
                                        <th className="pb-3 font-medium">Nhân viên phụ trách</th>
                                        <th className="pb-3 font-medium">Lần gọi gần nhất</th>
                                        <th className="pb-3 font-medium w-1/3">Ghi chú nổi bật</th>
                                    </tr>
                                </thead>
                                <tbody className="text-sm divide-y divide-gray-100">
                                    {[
                                        { service: 'Chăm sóc da mặt collagen', calls: 12, nv: 'tele admin, tp03', lastCall: '25/07/2026', note: 'Khách hài lòng, hẹn mua thêm liệu trình' },
                                        { service: 'Gói ủ trắng body', calls: 8, nv: 'tele01, TV Huyền', lastCall: '20/07/2026', note: 'Da sáng lên, không kích ứng. Đã tư vấn thêm SP dưỡng.' },
                                        { service: 'Massage đá nóng 90p', calls: 5, nv: 'tp02', lastCall: '10/06/2026', note: 'Nhắc lịch hẹn sắp tới.' },
                                        { service: 'Tẩy tế bào chết toàn thân', calls: 3, nv: 'tele admin', lastCall: '06/06/2026', note: 'Khách bận, đã huỷ lịch đột xuất.' },
                                    ].map((row, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50/50 transition-colors group">
                                            <td className="py-4 text-gray-800 font-medium">{row.service}</td>
                                            <td className="py-4 text-indigo-600 font-bold text-center">{row.calls}</td>
                                            <td className="py-4 text-gray-600">{row.nv}</td>
                                            <td className="py-4 text-gray-600">{row.lastCall}</td>
                                            <td className="py-4 text-gray-500 text-xs">{row.note}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
}